import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { deleteProgressEntry, recordProgressEntries } from '@/api/progress/progressApi';
import type { ProgressEntry, ProgressEntryWriteDraft } from '@/api/progress/progressApi.types';
import {
  buildPatientProgressComparisonQueryKey,
  buildPatientProgressSeriesQueryKey,
  buildPatientTimelineQueryKey,
} from '@/constants/queryKeys';

function useProgressInvalidation() {
  const queryClient = useQueryClient();
  return function invalidate(patientId: string) {
    queryClient.invalidateQueries({ queryKey: buildPatientProgressSeriesQueryKey(patientId) });
    queryClient.invalidateQueries({
      queryKey: buildPatientProgressComparisonQueryKey(patientId),
    });
    queryClient.invalidateQueries({ queryKey: buildPatientTimelineQueryKey(patientId) });
  };
}

interface RecordProgressEntriesVariables {
  patientId: string;
  draft: ProgressEntryWriteDraft;
}

export function useRecordProgressEntriesMutation(): UseMutationResult<
  ProgressEntry[],
  Error,
  RecordProgressEntriesVariables
> {
  const invalidate = useProgressInvalidation();
  return useMutation({
    mutationFn: function record(variables: RecordProgressEntriesVariables) {
      return recordProgressEntries(variables.patientId, variables.draft);
    },
    onSuccess: function handleSuccess(_recordedEntries, variables) {
      invalidate(variables.patientId);
    },
  });
}

interface DeleteProgressEntryVariables {
  entryId: string;
  patientId: string;
}

export function useDeleteProgressEntryMutation(): UseMutationResult<
  void,
  Error,
  DeleteProgressEntryVariables
> {
  const invalidate = useProgressInvalidation();
  return useMutation({
    mutationFn: function remove(variables: DeleteProgressEntryVariables) {
      return deleteProgressEntry(variables.entryId);
    },
    onSuccess: function handleSuccess(_result, variables) {
      invalidate(variables.patientId);
    },
  });
}
